/**
 * Detector-aware Custom-input component for the FormatInferrer
 * `format_decision` HITL.
 *
 * Renders a different input shape per detector kind so the reviewer's
 * input matches the cleanser-side contract:
 *
 *   * `regex_mask`        — single text input ("[A-Z]{3}-\\d+")
 *   * `boolean_labels`    — two text inputs (True label, False label)
 *   * `enum`              — comma-separated text input + chip preview
 *   * `date_format`       — single text input (strftime format)
 *   * `numeric_precision` — number input + thousands-separator checkbox
 *
 * Validation lives in `services/formatSpecValidation.ts`. The component
 * holds reviewer input as a per-kind draft and surfaces the validator's
 * error message inline; the parent (HitlQueue) reads the validation
 * result via the `onChange` callback and gates the Confirm button on
 * `result.ok`.
 *
 * See `docs/v0.5_DEV_PLAN.md` §3.1 for the design rationale and
 * `docs/DECISIONS.md` 2026-04-25 v0.5 Stage A entry for the supersede
 * note on the prior single-string `{value: ...}` payload.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  CustomSpecDraft,
  CustomSpecKind,
  ValidationResult,
  emptyDraft,
  validateCustomSpec,
} from '../services/formatSpecValidation';

export interface CustomSpecInputProps {
  /** The detector kind to render input for. */
  kind: CustomSpecKind;
  /** Stable ID prefix for input `name`/`id` attributes (collisions across
   *  cards in the same queue are otherwise invisible bugs for screen
   *  readers + label associations). */
  idPrefix: string;
  /** Disabled while a parent dispatch is pending. */
  disabled?: boolean;
  /** Called on every keystroke / toggle with the latest validation
   *  result, so the parent can enable/disable Confirm and capture the
   *  payload's `spec` body when valid. */
  onChange: (result: ValidationResult) => void;
}

export function CustomSpecInput({
  kind,
  idPrefix,
  disabled = false,
  onChange,
}: CustomSpecInputProps) {
  const [draft, setDraft] = useState<CustomSpecDraft>(() => emptyDraft(kind));

  // Re-seed the draft if the kind changes (e.g. parent swaps between
  // candidates of different kinds). Without this, switching kinds
  // silently keeps the previous kind's input fields populated.
  useEffect(() => {
    setDraft(emptyDraft(kind));
  }, [kind]);

  const result = useMemo(() => validateCustomSpec(draft), [draft]);

  // Notify the parent of every validation change. useEffect not inline
  // call so React doesn't warn about state updates during render.
  useEffect(() => {
    onChange(result);
    // onChange identity may change on every render of the parent; we
    // only want to fire when the validation result changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const errorMessage = result.ok ? null : result.error;

  return (
    <div className="space-y-2" data-testid="custom-spec-input">
      {kind === 'regex_mask' && (
        <RegexMaskInput
          idPrefix={idPrefix}
          disabled={disabled}
          value={'pattern' in draft ? draft.pattern : ''}
          onChange={(pattern) => setDraft({ kind: 'regex_mask', pattern })}
        />
      )}

      {kind === 'boolean_labels' && (
        <BooleanLabelsInput
          idPrefix={idPrefix}
          disabled={disabled}
          trueLabel={'trueLabel' in draft ? draft.trueLabel : ''}
          falseLabel={'falseLabel' in draft ? draft.falseLabel : ''}
          onChange={(trueLabel, falseLabel) =>
            setDraft({ kind: 'boolean_labels', trueLabel, falseLabel })
          }
        />
      )}

      {kind === 'enum' && (
        <EnumInput
          idPrefix={idPrefix}
          disabled={disabled}
          valuesCsv={'valuesCsv' in draft ? draft.valuesCsv : ''}
          onChange={(valuesCsv) => setDraft({ kind: 'enum', valuesCsv })}
        />
      )}

      {kind === 'date_format' && (
        <DateFormatInput
          idPrefix={idPrefix}
          disabled={disabled}
          value={'inputFormat' in draft ? draft.inputFormat : ''}
          onChange={(inputFormat) =>
            setDraft({ kind: 'date_format', inputFormat })
          }
        />
      )}

      {kind === 'numeric_precision' && (
        <NumericPrecisionInput
          idPrefix={idPrefix}
          disabled={disabled}
          decimalPlaces={
            'decimalPlaces' in draft ? draft.decimalPlaces : ''
          }
          thousandsSeparator={
            'thousandsSeparator' in draft ? draft.thousandsSeparator : false
          }
          onChange={(decimalPlaces, thousandsSeparator) =>
            setDraft({
              kind: 'numeric_precision',
              decimalPlaces,
              thousandsSeparator,
            })
          }
        />
      )}

      {errorMessage && (
        <p
          role="alert"
          data-testid="custom-spec-error"
          className="text-xs text-danger m-0"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-kind sub-components
// ---------------------------------------------------------------------------

function RegexMaskInput({
  idPrefix,
  disabled,
  value,
  onChange,
}: {
  idPrefix: string;
  disabled: boolean;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = `${idPrefix}-regex`;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-xs text-muted">
        Regex pattern
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="[A-Z]{3}-\d+"
        className="border border-border rounded px-2 py-0.5 text-sm font-mono bg-paper flex-1"
        aria-describedby={`${idPrefix}-regex-help`}
      />
      <span id={`${idPrefix}-regex-help`} className="sr-only">
        Pattern is enforced as a Python regex on each cell value.
      </span>
    </div>
  );
}

function BooleanLabelsInput({
  idPrefix,
  disabled,
  trueLabel,
  falseLabel,
  onChange,
}: {
  idPrefix: string;
  disabled: boolean;
  trueLabel: string;
  falseLabel: string;
  onChange: (t: string, f: string) => void;
}) {
  const trueId = `${idPrefix}-bool-true`;
  const falseId = `${idPrefix}-bool-false`;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label htmlFor={trueId} className="text-xs text-muted">
        True label
      </label>
      <input
        id={trueId}
        type="text"
        value={trueLabel}
        onChange={(e) => onChange(e.target.value, falseLabel)}
        disabled={disabled}
        placeholder="Y"
        className="border border-border rounded px-2 py-0.5 text-sm font-mono bg-paper w-20"
      />
      <label htmlFor={falseId} className="text-xs text-muted">
        False label
      </label>
      <input
        id={falseId}
        type="text"
        value={falseLabel}
        onChange={(e) => onChange(trueLabel, e.target.value)}
        disabled={disabled}
        placeholder="N"
        className="border border-border rounded px-2 py-0.5 text-sm font-mono bg-paper w-20"
      />
    </div>
  );
}

function EnumInput({
  idPrefix,
  disabled,
  valuesCsv,
  onChange,
}: {
  idPrefix: string;
  disabled: boolean;
  valuesCsv: string;
  onChange: (csv: string) => void;
}) {
  const id = `${idPrefix}-enum`;
  // Compute a chip preview of the parsed values so reviewers see how
  // the comma-separated input is being interpreted before they confirm.
  const previewValues = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of valuesCsv.split(',').map((v) => v.trim()).filter(Boolean)) {
      if (!seen.has(raw)) {
        seen.add(raw);
        out.push(raw);
      }
    }
    return out;
  }, [valuesCsv]);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <label htmlFor={id} className="text-xs text-muted whitespace-nowrap">
          Values (comma-separated)
        </label>
        <input
          id={id}
          type="text"
          value={valuesCsv}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Active, Inactive, Pending"
          className="border border-border rounded px-2 py-0.5 text-sm font-mono bg-paper flex-1"
        />
      </div>
      {previewValues.length > 0 && (
        <div
          className="flex flex-wrap gap-1 pt-1"
          data-testid="enum-preview-chips"
        >
          {previewValues.map((v) => (
            <span
              key={v}
              className="inline-flex items-center rounded-sm bg-brand/10 text-brand-dark px-2 py-0.5 text-xs font-mono"
            >
              {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function DateFormatInput({
  idPrefix,
  disabled,
  value,
  onChange,
}: {
  idPrefix: string;
  disabled: boolean;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = `${idPrefix}-date`;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-xs text-muted whitespace-nowrap">
        Date format
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="%Y-%m-%d"
        className="border border-border rounded px-2 py-0.5 text-sm font-mono bg-paper flex-1"
        aria-describedby={`${idPrefix}-date-help`}
      />
      <span id={`${idPrefix}-date-help`} className="sr-only">
        Use Python strftime directives. %Y is a 4-digit year, %m is the
        month, %d is the day, %H is hours, %M is minutes.
      </span>
    </div>
  );
}

function NumericPrecisionInput({
  idPrefix,
  disabled,
  decimalPlaces,
  thousandsSeparator,
  onChange,
}: {
  idPrefix: string;
  disabled: boolean;
  decimalPlaces: string;
  thousandsSeparator: boolean;
  onChange: (dp: string, sep: boolean) => void;
}) {
  const dpId = `${idPrefix}-num-dp`;
  const sepId = `${idPrefix}-num-sep`;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label htmlFor={dpId} className="text-xs text-muted">
        Decimal places
      </label>
      <input
        id={dpId}
        type="number"
        min={0}
        max={10}
        value={decimalPlaces}
        onChange={(e) => onChange(e.target.value, thousandsSeparator)}
        disabled={disabled}
        placeholder="2"
        className="border border-border rounded px-2 py-0.5 text-sm font-mono bg-paper w-20"
      />
      <label
        htmlFor={sepId}
        className="inline-flex items-center gap-1 text-xs text-muted cursor-pointer"
      >
        <input
          id={sepId}
          type="checkbox"
          checked={thousandsSeparator}
          onChange={(e) => onChange(decimalPlaces, e.target.checked)}
          disabled={disabled}
          className="accent-brand-dark"
        />
        Thousands separator
      </label>
    </div>
  );
}
